import React, { useState } from "react";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../Common/AppOverlay";
import "../../pages/Standards/Standards.scss";

const blankRisk = {
  id: 0,
  riskCategoryId: "",
  code: "",
  name: "",
  description: "",
  riskType: "Inherent",
  defaultInherentRiskLevel: "Medium",
  defaultControlRiskLevel: "Medium",
  defaultRiskLevel: "Medium",
  requiresResponseByDefault: false,
  sortOrder: 0,
};

const defaultRiskLevels = ["Low", "Medium", "High"];
const defaultRiskTypes = ["Inherent", "Control"];

const RiskDrawer = ({
  categories = [],
  riskLevels = defaultRiskLevels,
  riskTypes = defaultRiskTypes,
  isOpen,
  onClose,
  onSave,
  risk = null,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const isEditing = Boolean(risk?.id);
  const availableRiskLevels = riskLevels.length
    ? riskLevels
    : defaultRiskLevels;
  const availableRiskTypes = riskTypes.length ? riskTypes : defaultRiskTypes;
  const configuredBlankRisk = {
    ...blankRisk,
    riskType: availableRiskTypes[0],
    defaultInherentRiskLevel:
      availableRiskLevels.find((level) => level.toLowerCase() === "medium") ||
      availableRiskLevels[0],
    defaultControlRiskLevel:
      availableRiskLevels.find((level) => level.toLowerCase() === "medium") ||
      availableRiskLevels[0],
    defaultRiskLevel:
      availableRiskLevels.find((level) => level.toLowerCase() === "medium") ||
      availableRiskLevels[0],
  };
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: risk
      ? {
          ...configuredBlankRisk,
          ...risk,
          riskCategoryId: String(risk.riskCategoryId || ""),
        }
      : configuredBlankRisk,
    validationSchema: Yup.object({
      riskCategoryId: Yup.string().required("Select a category."),
      code: Yup.string().trim().required("Enter a risk code."),
      name: Yup.string().trim().required("Enter a risk name."),
    }),
    onSubmit: async (values) => {
      if (submitting) return;
      setSubmitting(true);
      try {
        await onSave({
          ...values,
          id: Number(values.id) || 0,
          riskCategoryId: Number(values.riskCategoryId),
          sortOrder: Number(values.sortOrder) || 0,
        });
        onClose();
      } catch {
        // The caller owns error reporting because persistence differs by context.
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Offcanvas
      backdrop="static"
      className="standards-drawer"
      direction="end"
      isOpen={isOpen}
      onClosed={() => validation.resetForm()}
      toggle={onClose}
    >
      <OffcanvasHeader className="canvasHeader" toggle={onClose}>
        {isEditing ? "Edit Risk" : "New Risk"}
      </OffcanvasHeader>
      <OffcanvasBody>
        <Form
          className="standard-drawer-form"
          onSubmit={(event) => {
            event.preventDefault();
            validation.handleSubmit();
          }}
        >
          <div className="standard-drawer-form__content">
            <p className="standard-drawer-form__intro">
              Define the defaults used when this risk is added to an inspection.
            </p>
            <section className="standard-drawer-form__section">
              <FormGroup>
                <Label>
                  Risk Category <b>*</b>
                </Label>
                <Input
                  type="select"
                  name="riskCategoryId"
                  value={validation.values.riskCategoryId}
                  onChange={validation.handleChange}
                  invalid={Boolean(
                    validation.touched.riskCategoryId &&
                      validation.errors.riskCategoryId
                  )}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Input>
                {validation.touched.riskCategoryId && (
                  <FormFeedback>
                    {validation.errors.riskCategoryId}
                  </FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label>
                  Risk Code <b>*</b>
                </Label>
                <Input
                  name="code"
                  value={validation.values.code}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  placeholder="e.g. COMPLEX_TRANSACTIONS"
                  invalid={Boolean(
                    validation.touched.code && validation.errors.code
                  )}
                />
                {validation.touched.code && (
                  <FormFeedback>{validation.errors.code}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label>
                  Risk Name <b>*</b>
                </Label>
                <Input
                  name="name"
                  value={validation.values.name}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={Boolean(
                    validation.touched.name && validation.errors.name
                  )}
                />
                {validation.touched.name && (
                  <FormFeedback>{validation.errors.name}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Description</Label>
                <Input
                  name="description"
                  type="textarea"
                  rows="3"
                  value={validation.values.description || ""}
                  onChange={validation.handleChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>Risk Type</Label>
                <Input
                  type="select"
                  name="riskType"
                  value={validation.values.riskType}
                  onChange={validation.handleChange}
                >
                  {availableRiskTypes.map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Inherent Risk</Label>
                <Input
                  type="select"
                  name="defaultInherentRiskLevel"
                  value={validation.values.defaultInherentRiskLevel}
                  onChange={validation.handleChange}
                >
                  {availableRiskLevels.map((level) => (
                    <option key={level}>{level}</option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Control Risk</Label>
                <Input
                  type="select"
                  name="defaultControlRiskLevel"
                  value={validation.values.defaultControlRiskLevel}
                  onChange={validation.handleChange}
                >
                  {availableRiskLevels.map((level) => (
                    <option key={level}>{level}</option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Risk Level</Label>
                <Input
                  type="select"
                  name="defaultRiskLevel"
                  value={validation.values.defaultRiskLevel}
                  onChange={validation.handleChange}
                >
                  {availableRiskLevels.map((level) => (
                    <option key={level}>{level}</option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup check>
                <Input
                  type="checkbox"
                  name="requiresResponseByDefault"
                  checked={validation.values.requiresResponseByDefault}
                  onChange={validation.handleChange}
                />
                <Label check>Require a response by default</Label>
              </FormGroup>
            </section>
          </div>
          <footer className="standard-drawer-form__actions">
            <Button
              className="standard-drawer-form__cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              className="standard-drawer-form__submit"
              disabled={submitting}
              type="submit"
            >
              {submitting
                ? "Saving..."
                : isEditing
                ? "Save Changes"
                : "Create Risk"}
            </Button>
          </footer>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default RiskDrawer;
